import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useApp } from './contexts/AppContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import EditorRoute from './components/EditorRoute';

// Import your components
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Bible from './components/Bible';
import BibleBook from './components/BibleBook';
import BibleChapter from './components/BibleChapter';
import Family from './components/Family';
import ReadingReports from './components/ReadingReports';
import ReaderProfile from './components/ReaderProfile';
import Chat from './components/Chat';
import AdminDashboard from './components/AdminDashboard';
import AdminApps from './components/AdminApps';
import AdminUsers from './components/AdminUsers';
import AdminRoles from './components/AdminRoles';
import CourseManagement from './components/CourseManagement';
import CourseEditor from './components/CourseEditor';
import CourseView from './components/CourseView';
import LessonManagement from './components/LessonManagement';
import LessonEditor from './components/LessonEditor';
import LessonView from './components/LessonView';
import NotFound from './components/NotFound';
import TestConfig from './pages/TestConfig';

const AppRoutes = () => {
  const { appConfig, hasFeature } = useApp();
  const { loading: authLoading, currentUser } = useAuth();

  // Debug logging
  console.log('AppRoutes rendering, features:', appConfig?.features);
  console.log('Has courses feature:', hasFeature('courses'));

  if (authLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Checking authentication status...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Root path redirects to dashboard if authenticated, auth if not */}
      <Route 
        path="/" 
        element={
          <React.Fragment>
            {currentUser ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth" replace />}
          </React.Fragment>
        } 
      />
      <Route path="/auth" element={<Auth />} />

      {/* Test Config Route - for multi-tenant testing */}
      <Route path="/test-config" element={<TestConfig />} />

      {/* Dashboard Route - will redirect to courses */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Bible feature routes */}
      {hasFeature('bible') && (
        <>
          <Route
            path="/bible"
            element={
              <ProtectedRoute>
                <Bible />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bible/:bookId"
            element={
              <ProtectedRoute>
                <BibleBook />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bible/:bookId/:chapterId"
            element={
              <ProtectedRoute>
                <BibleChapter />
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* Family feature routes */}
      {hasFeature('family') && (
        <>
          <Route
            path="/family"
            element={
              <ProtectedRoute>
                <Family />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reading-reports"
            element={
              <ProtectedRoute>
                <ReadingReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reading-reports/:readerId"
            element={
              <ProtectedRoute>
                <ReadingReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reader/:readerId"
            element={
              <ProtectedRoute>
                <ReaderProfile />
              </ProtectedRoute>
            }
          />
        </>
      )}

      {/* Chat feature route */}
      {hasFeature('chat') && (
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
      )}

      {/* Guaranteed Course Routes - not dependent on feature flags */}
      <Route
        path="/courses-direct"
        element={
          <ProtectedRoute>
            <CourseView isListView={true} />
          </ProtectedRoute>
        }
      />

      {/* Course Routes - Always enabled */}
      <>
        {console.log('Rendering courses routes - unconditionally')}
        {/* Public course listing for all authenticated users */}
        <Route
          path="/courses"
          element={
            <ProtectedRoute>
              <CourseView isListView={true} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId"
          element={
            <ProtectedRoute>
              <CourseView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/lessons/:lessonId"
          element={
            <ProtectedRoute>
              <LessonView />
            </ProtectedRoute>
          }
        />
      </>
      
      {/* Editor-only course management routes - still dependent on feature flag */}
      {hasFeature('courses') ? (
        <>
          {console.log('Rendering editor-specific courses routes')}
          <Route
            path="/apps/:appId/courses"
            element={
              <EditorRoute>
                <CourseManagement />
              </EditorRoute>
            }
          />
          <Route
            path="/apps/:appId/courses/new"
            element={
              <EditorRoute>
                <CourseEditor />
              </EditorRoute>
            }
          />
          <Route
            path="/apps/:appId/courses/:courseId/edit"
            element={
              <EditorRoute>
                <CourseEditor />
              </EditorRoute>
            }
          />
          <Route
            path="/apps/:appId/courses/:courseId/lessons"
            element={
              <EditorRoute>
                <LessonManagement />
              </EditorRoute>
            }
          />
          <Route
            path="/apps/:appId/courses/:courseId/lessons/new"
            element={
              <EditorRoute>
                <LessonEditor />
              </EditorRoute>
            }
          />
          <Route
            path="/apps/:appId/courses/:courseId/lessons/:lessonId/edit"
            element={
              <EditorRoute>
                <LessonEditor />
              </EditorRoute>
            }
          />
        </>
      ) : null}

      {/* Super Admin Routes */}
      <Route
        path="/admin"
        element={
          <SuperAdminRoute>
            <AdminDashboard />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/apps"
        element={
          <SuperAdminRoute>
            <AdminApps />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <SuperAdminRoute>
            <AdminUsers />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <SuperAdminRoute>
            <AdminRoles />
          </SuperAdminRoute>
        }
      />

      {/* Catch-all route for 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes; 